import { PureComponent, ReactElement, ReactNode } from 'react';
import PropTypes from 'prop-types';
import Button from 'antd/lib/button/button';

import { LINKS } from 'utils/constants/constants';
import error500Icn from 'assets/img/icons/error500-icn.svg';

interface Props {
  children: ReactNode;
}

interface State {}

const sectionClasses = 'flex flex-col justify-center h-screen bg-black-0 text-black-70 text-center '
  + 'dark:bg-black-85 dark:text-black-30';

class ErrorBoundary extends PureComponent<Props, State> {
  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  };

  public state = {
    count: 0,
    skipErrorBoundary: true, // process.env.NODE_ENV === 'development'
    error: undefined,
    info: undefined,
  };

  static getDerivedStateFromError(error: any, info: any) {
    return { error, info };
  }

  public componentDidMount() {
    const { skipErrorBoundary } = this.state;

    if (!skipErrorBoundary) {
      window.onunhandledrejection = (error) => console.error({ error });
      window.onerror = (message: any, url: any, line: any, column: any, error: any) => {
        const skipError = message.includes('Script error') && url === '' && line === 0 && column === 0 && !error;

        this.setState({ error, info: JSON.stringify(message) });
        !skipError && console.error({ error, info: { message, url, line, column } });
      };
    }
  }

  public componentDidCatch(error: any, info: any) {
    const { count, skipErrorBoundary } = this.state;

    this.setState({ count: count + 1 });

    // Circular render
    if (count >= 3) {
      this.setState({ skipErrorBoundary: false });
    }

    if (!skipErrorBoundary) {
      this.setState({ error, info: JSON.stringify(info) });
      console.error({ error, info });
    }
  }

  public render() {
    const { children } = this.props;
    const { skipErrorBoundary, error } = this.state;

    if (error && !skipErrorBoundary) {
      return (
        <section className={sectionClasses}>
          <div className="flex items-center justify-center flex-col my-0 mx-auto max-w-xl flex-1">
            <img src={error500Icn} alt="connection-error-image" />
            <h1 className="mt-12">Our servers needed a break</h1>
            <p className="mx-0 py-6">
              Let&apos;s give them some time to recharge while our technical support specialist will investigate this.
              Please try to reload the page by clicking the button below.
              If the error is still here for a while, please let the support team know.
            </p>
            <Button
              type="primary"
              className="text-black-0 dark:text-black-90"
              onClick={() => document.location.reload()}
            >
              Reload this page
            </Button>
          </div>
          <a href={LINKS.autoDs.status} rel="noopener noreferrer" target="_blank" className="text-blue-50 mb-10">
            Visit our status page
          </a>
        </section>
      );
    }

    return (children as ReactElement);
  }
}

export default ErrorBoundary;
